import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import type { Module } from '@/types/modules'
import { getModuleMeta } from '@/types/modules'

interface EditableModuleTitleProps {
  module: Module
  conversationId?: string
  className?: string
  fallbackLabel?: string
  /**
   * Opt-in inline rename (#3598). When provided, double-click enters edit mode
   * and the trimmed value is committed via this callback on Enter/blur (Esc
   * cancels). When omitted, the title stays a static label and `startEdit` is a
   * no-op — so existing pane consumers (Calendar/Drive/Timeline/NoteGrid) that
   * call `startEdit` without wiring persistence keep their current behavior and
   * never silently discard a rename.
   */
  onSave?: (label: string) => void
  [legacyProp: string]: unknown
}

export interface EditableModuleTitleRef {
  startEdit: () => void
}

export const EditableModuleTitle = forwardRef<EditableModuleTitleRef, EditableModuleTitleProps>(
  (props, ref) => {
    const { module, className, fallbackLabel, onSave } = props
    const meta = getModuleMeta(module.type)
    // User-set label always wins; fall back to the catalog default, then the
    // caller-provided fallback. (#3598) Standard modules now honor a custom
    // label so a persisted rename shows in the pane title.
    const displayLabel = module.label || meta?.defaultLabel || fallbackLabel || ''
    const editable = typeof onSave === 'function'

    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const startEdit = () => {
      if (!editable) return
      setDraft(displayLabel)
      setIsEditing(true)
      // Focus + select after the input mounts.
      requestAnimationFrame(() => {
        const el = inputRef.current
        if (!el) return
        el.focus()
        el.select()
      })
    }

    // startEdit stays a no-op unless an onSave handler opts the consumer in.
    useImperativeHandle(ref, () => ({ startEdit }))

    const commit = () => {
      if (!editable) return
      const next = draft.trim()
      setIsEditing(false)
      if (next && next !== displayLabel) onSave(next)
    }

    const cancel = () => {
      setIsEditing(false)
    }

    if (editable && isEditing) {
      return (
        <input
          ref={inputRef}
          className={`module-title module-title-input ${className ?? ''}`.trim()}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
        />
      )
    }

    return (
      <h3
        className={`module-title ${className ?? ''}`.trim()}
        onDoubleClick={editable ? startEdit : undefined}
      >
        {displayLabel}
      </h3>
    )
  }
)

EditableModuleTitle.displayName = 'EditableModuleTitle'
